import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText } from "@/components";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { ReadOnlyRichTextField } from "@/components/companies/shared/ReadOnlyField";

import { useUpdateSetJobIntake } from "@/hooks/updateSets/useUpdateSetJobIntake";
import { JobIntake } from "@/types";

const cultureSchema = z.object({
  teamDescription: z.string().nullable(),
  managerDetails: z.string().nullable(),
  successfulTraitsAndPersonalities: z.string().nullable(),
  failingBehaviorsAndPersonalities: z.string().nullable(),
});

export const JobCulture = ({
  isEditing,
  jobIntake,
}: {
  isEditing: boolean;
  jobIntake: JobIntake;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateJobIntake, isError } = useUpdateSetJobIntake();

  /* Misc Hooks */
  const culture = jobIntake.culture;
  const form = useForm<z.infer<typeof cultureSchema>>({
    resolver: zodResolver(cultureSchema),
    defaultValues: culture,
  });

  /* Handlers */
  const handleSave = (data: z.infer<typeof cultureSchema>) => {
    updateJobIntake({
      updateJobIntakeRequest: { culture: data },
    });
  };

  const saveOnBlur = () => {
    void form.handleSubmit(handleSave)();
  };

  return (
    <Column className="gap-6">
      {isEditing ? (
        <IntakeForm form={form} onSave={saveOnBlur}>
          <Column className="gap-6">
            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="teamDescription"
              form={form}
              infoTooltip={
                "Describe the team as it stands currently.\nWhat are some of the personalities and hierarchy?\nWhat's the mood? Is it no BS, humorous, somewhere in the middle?"
              }
              label="Team Description"
              onBlur={saveOnBlur}
            />
            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="managerDetails"
              form={form}
              infoTooltip={
                "Who does this person report to and what do they expect?"
              }
              label="Manager Details"
              onBlur={saveOnBlur}
            />
            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="successfulTraitsAndPersonalities"
              form={form}
              infoTooltip={
                "What traits or personalities thrive here?\nExamples: family-oriented, career-oriented, confident and experts in their craft, high-growth and ambitious, rugged, nerdy, etc"
              }
              label="Successful Traits & Personalities"
              onBlur={saveOnBlur}
            />
            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="failingBehaviorsAndPersonalities"
              form={form}
              infoTooltip={"What behaviors or personalities fail here?"}
              label="Failing Behaviors & Personalities"
              onBlur={saveOnBlur}
            />
            {isError && (
              <ErrorText errorMessage="An error occurred while saving. Please try again." />
            )}
          </Column>
        </IntakeForm>
      ) : (
        <Column className="gap-4">
          <ReadOnlyRichTextField
            label="Team Description"
            value={culture.teamDescription}
          />
          <ReadOnlyRichTextField
            label="Manager Details"
            value={culture.managerDetails}
          />
          <ReadOnlyRichTextField
            label="Successful Traits & Personalities"
            value={culture.successfulTraitsAndPersonalities}
          />
          <ReadOnlyRichTextField
            label="Failing Behaviors & Personalities"
            value={culture.failingBehaviorsAndPersonalities}
          />
        </Column>
      )}
    </Column>
  );
};
